// File: frontend/src/components/Contacts/ContactListItem.jsx
// Single contact row — responsive: collapses into a stacked card below md.

import React from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  PhoneIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { TagsDisplay } from '../Tags/TagManager';
import {
  getScoreColor,
  formatLeadScore,
  getLifecycleColor,
  getStatusColor,
  formatLifecycleStage,
  formatStatus,
} from './contactFormat';

const ContactListItem = ({ contact, onEdit, onDelete, deleteLoading }) => {
  return (
    <div className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {/* Avatar + info */}
        <div className="flex items-start sm:items-center space-x-4">
          <div className="flex-shrink-0">
            {contact.avatar_url ? (
              <img
                className="w-12 h-12 rounded-full"
                src={contact.avatar_url}
                alt={`${contact.first_name} ${contact.last_name}`}
              />
            ) : (
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-medium text-lg">
                  {contact.first_name?.[0]}
                  {contact.last_name?.[0]}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <Link to={`/contacts/${contact.id}`}>
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600">
                  {contact.first_name} {contact.last_name}
                </h4>
              </Link>
              {contact.lead_score && (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getScoreColor(
                    contact.lead_score
                  )}`}
                >
                  {formatLeadScore(contact.lead_score)}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
              {contact.title && `${contact.title} `}
              {contact.company && `em ${contact.company.name}`}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-1 min-w-0">
                <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{contact.email}</span>
              </div>
              <div className="flex items-center space-x-1">
                <PhoneIcon className="h-4 w-4 flex-shrink-0" />
                <span>{contact.phone}</span>
              </div>
            </div>

            <div className="flex items-center flex-wrap gap-2 mt-2">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getLifecycleColor(
                  contact.lifecycle_stage
                )}`}
              >
                {formatLifecycleStage(contact.lifecycle_stage)}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                  contact.lead_status
                )}`}
              >
                {formatStatus(contact.lead_status)}
              </span>
              <TagsDisplay tags={contact.tags || []} size="xs" maxVisible={3} showCount={true} />
            </div>
          </div>
        </div>

        {/* Stats + actions */}
        <div className="flex items-center justify-between md:justify-end gap-6 md:flex-shrink-0 pl-16 md:pl-0">
          <div className="text-left md:text-right">
            {contact.potential_value && (
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                R$ {(contact.potential_value / 1000).toFixed(0)}k
              </div>
            )}
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {contact.activities_count || 0} atividades
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {contact.last_activity_at
                ? `Última atividade: ${new Date(contact.last_activity_at).toLocaleDateString('pt-BR')}`
                : 'Sem atividades'}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Link to={`/contacts/${contact.id}`}>
              <button className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg" title="Ver detalhes">
                <EyeIcon className="h-5 w-5" />
              </button>
            </Link>
            <button
              onClick={() => onEdit(contact)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 rounded-lg"
              title="Editar contato"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onDelete(contact)}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
              title="Excluir contato"
              disabled={deleteLoading}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactListItem;
